import { apiClient } from './api-client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  avatar?: string | null;
  role: 'user' | 'provider' | 'admin';
  profile?: unknown;
}

export interface AuthResponse {
  message?: string;
  user: AuthUser;
  accessToken?: string;
  expiresIn?: number;
  cookieSession?: boolean;
  token?: {
    type: string;
    token: {
      type: string;
      name: string | null;
      token: string;
      abilities: string[];
    };
    lastUsedAt: string | null;
    expiresAt: string;
  };
}

const USER_KEY = 'admin_user_data';
const LEGACY_TOKEN_KEYS = ['admin_token', 'access_token', 'token', 'authToken'] as const;

function persistUser(user: AuthUser) {
  try {
    if (user.role !== 'admin') {
      clearAllAuthStorage();
      return;
    }
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.removeItem(USER_KEY);
    for (const key of LEGACY_TOKEN_KEYS) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

function clearAllAuthStorage() {
  try {
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_KEY);
    for (const key of LEGACY_TOKEN_KEYS) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

function clearUser() {
  clearAllAuthStorage();
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Prefer httpOnly cookie session for Admin SPA.
      const response = await apiClient.post<AuthResponse>('/api/auth/sso/admin-login', credentials);
      const data = (response.data || (response as unknown as AuthResponse)) as AuthResponse;
      if (!data?.user) {
        throw new Error('No data received from server');
      }
      // Server-enforced admin gate — never trust a client-only role claim.
      if (data.user.role !== 'admin') {
        apiClient.setToken(null);
        apiClient.clearSessionSecrets();
        clearAllAuthStorage();
        throw new Error('Access denied. Admin role required.');
      }

      // Cookie session is authoritative; keep CSRF enforceable (no memory Bearer).
      apiClient.preferCookieSession();
      persistUser(data.user);
      return data;
    } catch (error) {
      apiClient.setToken(null);
      apiClient.clearSessionSecrets();
      clearAllAuthStorage();
      if (error instanceof Error) throw error;
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  /**
   * Re-validate session against the server. Local role storage is UX-only —
   * non-admin or failed session checks clear all credentials.
   */
  async verifyAdminSession(): Promise<AuthUser> {
    const res = await apiClient.get<{ user?: AuthUser } & AuthUser>('/api/auth/session');
    const payload = res.data as ({ user?: AuthUser } & Partial<AuthUser>) | undefined;
    const u: AuthUser | undefined =
      payload?.user ||
      (payload?.role && payload?.id && payload?.email
        ? (payload as AuthUser)
        : undefined) ||
      (res as unknown as { user?: AuthUser }).user;
    if (!u || u.role !== 'admin') {
      await this.logout();
      throw new Error('Access denied. Admin role required.');
    }
    persistUser(u);
    return u;
  }

  async logout() {
    try {
      await apiClient.post('/api/auth/sso/logout');
    } catch {
      /* still clear local state */
    }
    apiClient.setToken(null);
    apiClient.clearSessionSecrets();
    apiClient.preferCookieSession();
    clearUser();
  }

  /**
   * Optimistic UX hint only — never grant access from this alone.
   * Real authorization requires {@link verifyAdminSession} / cookie session.
   */
  isAuthenticated(): boolean {
    try {
      return Boolean(sessionStorage.getItem(USER_KEY) || apiClient.getToken());
    } catch {
      return Boolean(apiClient.getToken());
    }
  }

  getToken(): string | null {
    return apiClient.getToken();
  }

  getStoredUser(): AuthUser | null {
    try {
      const raw = sessionStorage.getItem(USER_KEY);
      if (!raw) return null;
      const user = JSON.parse(raw) as AuthUser;
      if (user?.role !== 'admin') {
        clearAllAuthStorage();
        return null;
      }
      return user;
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
export default authService;
